import { useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { useCardData } from '../CardDataContext';
import { isLegacyCard } from '../../utils/isLegacyCard';
import { useCardLocationContext } from '../CardLocationContext';
import { useDispatch } from './LocationStateContext';
import mark from 'crm-fe-perf/timing/mark';
import measure from 'crm-fe-perf/timing/measure';
import { getCardRenderFinish } from 'crm-fe-perf/timing/marks/crm-cards-sdk/performanceMarks';
import { getRealContentStartToFinish } from 'crm-fe-perf/timing/measurements/crm-cards-sdk/performanceMeasurements';
import { jsx as _jsx } from "react/jsx-runtime";
function useShouldSendPerfMessage(isInViewport, isRenderedOffscreen) {
  const cardLocationContext = useCardLocationContext();
  const shouldSendMessage =
  // NO VIRTUALIZATION: Wait to send message until the card element has been rendered, which gives us the initial "isInViewport" value
  cardLocationContext.enableVirtualization === false && isInViewport !== undefined ||
  // WITH VIRTUALIZATION: Only start measuring perf after the card starts rendering
  cardLocationContext.enableVirtualization && (isInViewport || isRenderedOffscreen);
  return shouldSendMessage;
}
export function useCardRenderStarted(cardType, cardId, isInViewport, isRenderedOffscreen = false, tabIndex) {
  const didSendRenderStartedMessage = useRef(false);
  const shouldSendMessage = useShouldSendPerfMessage(isInViewport, isRenderedOffscreen);
  const dispatch = useDispatch();
  useLayoutEffect(() => {
    if (shouldSendMessage && !didSendRenderStartedMessage.current) {
      dispatch({
        cardType,
        cardId,
        type: 'CARD_LOADING_STARTED',
        startTimeMs: performance.now(),
        isInViewport: !!isInViewport,
        tabIndex
      });
      didSendRenderStartedMessage.current = true;
    }
  }, [cardType, cardId, isInViewport, shouldSendMessage, didSendRenderStartedMessage, dispatch, tabIndex]);
}
function createSuccessMarker({
  isCollapsed = false
} = {}) {
  return function SuccessMarker() {
    const {
      location,
      loggingTag
    } = useCardLocationContext();
    const {
      cardData,
      tabIndex,
      isInViewport,
      isRenderedOffscreen
    } = useCardData();
    const didSendSuccessMarker = useRef(false);
    const shouldSendMessage = useShouldSendPerfMessage(isInViewport, isRenderedOffscreen);
    const dispatch = useDispatch();
    if (!cardData) {
      throw new Error('SuccessMarker was called outside of a valid CardDataContext.');
    }
    const cardId = cardData.definition.cardId;
    const cardType = isLegacyCard(cardData) ? cardData.configuration.cardType : cardData.definition.cardType;
    const dimensions = useMemo(() => ({
      location,
      logging_tag: loggingTag || '',
      card_type: cardType,
      error: 'false'
    }), [cardType, location, loggingTag]);
    useEffect(() => {
      mark(getCardRenderFinish(cardType, cardId), dimensions, true);
      measure(getRealContentStartToFinish(cardType, cardId), Object.assign({}, dimensions, {
        start: '',
        // hardcode this to reduce cardinality
        end: '' // hardcode this to reduce cardinality
      }));
      // don't fire success marker until visibility is loaded
      if (shouldSendMessage && !didSendSuccessMarker.current) {
        dispatch({
          type: 'CARD_LOADING_FINISHED',
          finishedState: 'SUCCESS',
          cardId,
          cardType,
          isCollapsed,
          tabIndex
        });
        didSendSuccessMarker.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, shouldSendMessage]);
    return null;
  };
}
function createErrorMarker({
  type
} = {}) {
  return function ErrorMarker() {
    const {
      location,
      loggingTag
    } = useCardLocationContext();
    const {
      cardData,
      tabIndex,
      isInViewport,
      isRenderedOffscreen
    } = useCardData();
    const didSendErrorMarker = useRef(false);
    const shouldSendMessage = useShouldSendPerfMessage(isInViewport, isRenderedOffscreen);
    if (!cardData) {
      throw new Error('ErrorMarker was called outside of a valid CardDataContext.');
    }
    const dispatch = useDispatch();
    const cardId = cardData.definition.cardId;
    const cardType = isLegacyCard(cardData) ? cardData.configuration.cardType : cardData.definition.cardType;
    const dimensions = useMemo(() => ({
      location,
      logging_tag: loggingTag,
      card_type: cardType,
      error: 'true'
    }), [cardType, location, loggingTag]);
    useEffect(() => {
      mark(getCardRenderFinish(cardType, cardId), dimensions, true);
      measure(getRealContentStartToFinish(cardType, cardId), Object.assign({}, dimensions, {
        start: '',
        // hardcode this to reduce cardinality
        end: '' // hardcode this to reduce cardinality
      }));
      if (shouldSendMessage && !didSendErrorMarker.current) {
        didSendErrorMarker.current = true;
        dispatch({
          type: 'CARD_LOADING_FINISHED',
          finishedState: 'ERROR',
          cardId,
          cardType,
          tabIndex,
          errorType: type || 'HANDLED_IN_CARD'
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, shouldSendMessage]);
    return null;
  };
}
export function useCardRenderMarkers() {
  const {
    cardData,
    tabIndex,
    isInViewport,
    isRenderedOffscreen
  } = useCardData();
  const shouldSendMessage = useShouldSendPerfMessage(isInViewport, isRenderedOffscreen);
  const dispatch = useDispatch();
  if (!cardData) {
    throw new Error('useCardRenderMarkers was called outside of a valid CardDataContext.');
  }
  const cardId = cardData.definition.cardId;
  const cardType = isLegacyCard(cardData) ? cardData.configuration.cardType : cardData.definition.cardType;
  useEffect(() => {
    if (shouldSendMessage) {
      dispatch({
        type: 'CARD_PERF_HOOKS_CALLED',
        cardId,
        cardType,
        tabIndex
      });
    }
  }, [dispatch, cardId, cardType, shouldSendMessage, tabIndex]);
  return useMemo(() => ({
    SuccessMarker: createSuccessMarker(),
    ErrorMarker: createErrorMarker()
  }), []);
}

/**
 * This function should only be used by card formatters in order to prevent timeouts
 * when rendering cards that are collapsed by default.
 */
export function useCollapsedCardSuccessMarker() {
  return useMemo(() => ({
    SuccessMarker: createSuccessMarker({
      isCollapsed: true
    })
  }), []);
}
export function useCardErrorMarker({
  type
}) {
  return useMemo(() => createErrorMarker({
    type
  }), [type]);
}

/**
 * To be used in CardErrorBoundary so that it can render the error marker when an error occurs.
 * It's a class component so we can't use hooks inside it.
 */
export function withCardErrorMarker(Component, errorMarkerType) {
  const ErrorMarker = createErrorMarker({
    type: errorMarkerType
  });
  return function ComponentWithErrorMarker(props) {
    return /*#__PURE__*/_jsx(Component, Object.assign({}, props, {
      ErrorMarker: ErrorMarker
    }));
  };
}